<template>
  <div class="md-layout text-center">
    <label class="md-layout-item md-size-100">
      Auction leads
    </label>
    <router-link :to="`/application/${auctionId}/tools/sql-auction`"
      class="md-layout-item md-size-20 md-button md-primary">
      SQL<br>{{ get_auction_notification.sql_auction_count }}
    </router-link>
    <router-link :to="`/application/${auctionId}/tools/mql-auction`"
      class="md-layout-item md-size-20 md-button md-primary">
      MQL<br>{{ get_auction_notification.mql_auction_count }}
    </router-link>
    <router-link 
      to=""
      class="md-layout-item md-size-20 md-button md-success live-button"
      @click.native.prevent="handleLiveAuction"
      @mouseenter="onMouseEnter"
      ref="liveButton"
    >
      <span :class="{ 'blink_me': hasLiveAuctions }">Live<br>{{ get_auction_notification.live_auction_count }}</span>
      <md-tooltip v-if="hasLiveAuctions" :md-active.sync="tooltipActive" class="live-tooltip" md-direction="bottom">
        New LIVE Lead<br><b>Click this button now!</b>
      </md-tooltip>
    </router-link>

    <!-- Alerts section -->
    <div class="md-layout-item md-size-20  md-switch-container">
      Alerts
      <md-switch
        class="md-switch"
        v-model="auction_notification"
        @change="updateActionNotification"
      ></md-switch>
    </div>
  </div>
</template>


<script>
import { mapGetters } from "vuex";
import axios from "axios";

export default {
  data() {
    return {
      tooltipActive: false,  // State for tooltip visibility
      autoHideTimer: null,   // Timer reference for auto-hide
      auctionId: '',         // Store the dynamic auction ID from router
      auction_notification: false, // for auction notification
    };
  },
  mounted() {
    this.auction_notification = this.user.auction_notification;
  },
  watch: {
    // Close panel when no auctions left
    live_auction_previews(newVal) {
      if (!newVal || newVal.length === 0) {
          this.$store.commit("set_show_live_auction_drawer", false);
      }
    },
  },
  computed: {
    ...mapGetters(["user", "get_auction_notification", "get_last_auction_notification_timestamp", "show_live_auction_drawer", "live_auction_previews"]),
    auction_notification: {
      get() {
        return this.user.auction_notification; 
      },
      set(value) {
        this.updateActionNotification(value);
      },
    },
    hasLiveAuctions() {
      return Number(this.get_auction_notification?.live_auction_count || 0) > 0;
    },
  },
  methods: {
    async handleLiveAuction() {
      await this.$store.dispatch('fetch_live_auction_previews');
      // Show panel when Live clicked button just if live auctions not expired exists
      if (!this.live_auction_previews || this.live_auction_previews.length === 0) {
        this.$store.commit("set_show_live_auction_drawer", false);
        return;
      } else {
        const currentVisibilityDrawer = this.show_live_auction_drawer;
        this.$store.commit("set_show_live_auction_drawer", !currentVisibilityDrawer);
      }
    },
    async checkLiveAuctionSubscription() {
      let response = await axios.get(`/api/v1/zink_services`);
      return response.data.some((service) => {
        return service.name === 'Live Auction' && service.subscribed === true;
      });
    },
    async updateActionNotification(value) {
      const response = await axios.post('/api/v1/user/auction_notification', {
        auction_notification : value,
        id: this.user.id
      })
    },
    async subscribeToLiveAuctionPusher() {
      const channelName = `broker-subscription-live-auction-channel.${this.user.id}`;
      const { channel } = await this.$store.dispatch('subscribe_to_pusher_channel', channelName);
      if (channel === null) {
        return;
      }
      const vue = this;
      channel.bind('broker-subscription-live-auction', function(data) {
        // Update auction notification in vuex
        vue.$store.dispatch('update_auction_notification', data["message"]);

        // Check if it's a new auction
        if (!data["message"]["is_new"]) {
          return;
        }
        vue.playSound();

        // Check if throttle time has elapsed
        let throttleTime = process.env.MIX_AUCTION_NOTIFICATION_THROTTLE || 2;
        let elapsedTime = Date.now() - vue.get_last_auction_notification_timestamp;
        if (elapsedTime < throttleTime * 60 * 1000) {
          return;
        }
        vue.$store.dispatch('update_last_auction_notification_timestamp', Date.now());

        // Trigger the tooltip to show the live lead link
        vue.showTooltip();
      });
    },

    resetAutoHideTimer() {
      if (this.autoHideTimer) {
        clearTimeout(this.autoHideTimer); // Clear any existing timer
      }
      // Set a new 1-minute timer
      this.autoHideTimer = setTimeout(() => {
        this.tooltipActive = false;
      }, 60000); // Tooltip will disappear after 1 minute
    },

    pulsateLiveButton() {
      const liveButton = this.$refs.liveButton.$el;  // Access the actual DOM element via $el

      if (liveButton) {

        // First, remove the class if it already exists
        liveButton.classList.remove('pulsating-button');

        // Force a reflow so the class can reapply
        void liveButton.offsetWidth;  // Reflow trick

        // Then, add the pulsating class again
        liveButton.classList.add('pulsating-button');

        // Remove the pulsating effect after 30 seconds
        setTimeout(() => {
          liveButton.classList.remove('pulsating-button');
        }, 30000); // 30 seconds
      }
    },
    showTooltip() {
      if (!this.auction_notification) return;
      this.pulsateLiveButton();
      this.tooltipActive = true;
      this.resetAutoHideTimer();
    },
    playSound() {
      if (!this.auction_notification) return; // Do nothing if switch is off
      const audio = new Audio("/audio/notification-positive-marimba-swoop-1-00-00.mp3");
      audio.play();
    },
    onMouseEnter() {
      if (!this.auction_notification) return;
      this.pulsateLiveButton();
      this.tooltipActive = true; // Show tooltip on hover
      this.resetAutoHideTimer(); // Restart the 1-minute timer
    }
  },
  async created() {
    // Get the auctionId from the route params
    this.auctionId = this.$route.params.id;

    // Get first-load values before pusher notifications
    let response = await axios.get(`/api/v1/auction/leads-counts`);
    this.$store.dispatch('update_auction_notification', response.data);

    let isSubscribed = await this.checkLiveAuctionSubscription();
    if (isSubscribed) {
      this.subscribeToLiveAuctionPusher();
    }
  },
  beforeDestroy() {
    if (this.autoHideTimer) {
      clearTimeout(this.autoHideTimer); // Clear the timer when component is destroyed
    }
  }
};
</script>




<style lang="scss" scoped>
.blink_me {
  animation: blink-animation 1s steps(5, start) infinite;
  -webkit-animation: blink-animation 1s steps(5, start) infinite;
}
@keyframes blink-animation {
  to {
    visibility: hidden;
  }
}
@-webkit-keyframes blink-animation {
  to {
    visibility: hidden;
  }
}
.md-layout {

  label {
    margin-bottom: 5px;
    padding: 0;
    font-size: 0.6875rem;
    line-height: 5px !important;
    color: rgba(0, 0, 0, 0.54);
  }

  .md-button {
    height: 40px;
    margin: 0 2.5px;
    padding: 2.5px;
    text-transform: none;

    &.md-primary {
      background-color: #f18f43 !important;
    }
  }

  .md-switch-container {
    font-size: 13px;
    height: 40px;
    margin: 0 0;
    padding: 0px;
    text-transform: none;
  }

  .md-switch {
    transform: scale(0.7); 
    margin-top: 0px;
    margin-left: 5px; 
  }
}

.md-tooltip.live-tooltip {
  background-color: #ffd7b5 !important;
  color: darkred !important;
  border: solid 1px #f8c495;

  a {
    color: darkred !important;
    font-weight: bold;
    text-decoration: underline;
  }

  &[x-placement=bottom]::after {
    border-bottom-color: #f8c495 !important; /* Match the tooltip background */
  }
}

/* Add the pulse animation */
@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 85, 0, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 15px 10px rgba(0, 255, 0, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 255, 0, 0);
  }
}

/* Apply the animation */
.pulsating-button {
  animation: pulse 1.5s infinite;
}

</style>
